import type { Entry } from "../../shared/types";
import { clockTime, dayLabel, h } from "./dom";
import { Spring, drag, prefersReducedMotion, rubberband } from "./motion";

const DAY_MS = 86_400_000;
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ACTION_WIDTH = 96;
const THROW_VELOCITY = -1600;
const THROW_FRACTION = 0.6;

export function dayName(ts: number): string {
  return DAY_NAMES[new Date(ts).getDay()] ?? "";
}

export function isSameDay(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** "Monday. Kai." */
export function greeting(childName: string, now: number): string {
  return `${dayName(now)}. ${childName}.`;
}

export function lastEntryLine(entries: readonly Entry[], now: number): string {
  const latest = entries[0];
  if (!latest) return "Nothing logged yet.";
  const when = isSameDay(latest.ts, now)
    ? "today"
    : isSameDay(latest.ts, now - DAY_MS)
      ? "yesterday"
      : dayName(latest.ts).toLowerCase();
  return `Last entry ${when}, ${clockTime(latest.ts)}.`;
}

/** Quiet type: subjects and minutes are muted text. A flag is the one thing marked for the guide. */
export function entryTags(entry: Entry): HTMLElement | null {
  const parts = [
    ...entry.subjects.map((s) => h("span", { class: "tag" }, s)),
    entry.minutes > 0 && h("span", { class: "tag mono" }, `${entry.minutes} min`),
    entry.stuck === true && h("span", { class: "tag hl" }, "Got stuck"),
    entry.askGuide === true && h("span", { class: "tag hl" }, "Ask my guide"),
    entry.example === true && h("span", { class: "tag" }, "Example"),
  ].filter((p): p is HTMLElement => p !== false);
  if (parts.length === 0) return null;
  const tags = h("div", { class: "etags" });
  parts.forEach((part, i) => {
    if (i > 0) tags.appendChild(h("span", { class: "dot", "aria-hidden": "true" }, "·"));
    tags.appendChild(part);
  });
  return tags;
}

export interface EntryRowOptions {
  /** Called once the row has been swiped away or its Delete pressed. The page removes the entry and offers Undo. */
  onDelete: (entry: Entry) => void;
  /** Animate the row in. Only for the entry that was just saved. */
  grow?: boolean;
}

let openRow: { close: () => void } | null = null;

/**
 * One entry, swipeable. Drag left 1:1 to reveal Delete. A hard flick, or a drag
 * past most of the row, throws it away. Undo is the page's job, in the toast.
 */
export function entryRow(entry: Entry, options: EntryRowOptions): HTMLElement {
  const inner = h(
    "div",
    { class: "entry-inner" },
    h("div", { class: "etime" }, clockTime(entry.ts)),
    h(
      "div",
      { class: "ebody" },
      h("p", { class: "etext" }, entry.text || "(photo)"),
      entry.photo !== undefined && h("img", { class: "thumb", src: entry.photo, alt: "Photo attached to this entry" }),
      entryTags(entry),
    ),
  );
  const action = h(
    "button",
    {
      type: "button",
      class: "entry-action",
      "aria-label": `Delete the ${clockTime(entry.ts)} entry`,
      onclick: () => remove(),
    },
    "Delete",
  );
  const row = h("div", { class: "entry" }, action, inner);
  const wrapper = h("div", { class: options.grow ? "grow" : "" }, row);
  const x = new Spring(0, {
    response: 0.3,
    damping: 1,
    onUpdate: (value) => {
      inner.style.transform = `translate3d(${value}px, 0, 0)`;
    },
  });
  const me = { close: () => x.set(0, { damping: 1, response: 0.3 }) };
  let grabbed = 0;

  drag(inner, {
    axis: "x",
    onStart: () => {
      x.stop();
      grabbed = x.value;
      if (openRow && openRow !== me) openRow.close();
    },
    onMove: (dx) => {
      let value = grabbed + dx;
      if (value > 0) value = rubberband(value, 80);
      else if (value < -ACTION_WIDTH) value = -ACTION_WIDTH + rubberband(value + ACTION_WIDTH, row.offsetWidth);
      x.hold(value);
    },
    onEnd: (vx) => {
      const width = row.offsetWidth;
      if (vx < THROW_VELOCITY || x.value < -width * THROW_FRACTION) {
        x.set(-width, { velocity: vx, damping: 1, response: 0.25 });
        setTimeout(remove, 80);
        return;
      }
      // Direction of travel decides when the finger was moving. Then the spring picks up its speed.
      const openIt = Math.abs(vx) > 120 ? vx < 0 : x.value < -ACTION_WIDTH / 2;
      x.set(openIt ? -ACTION_WIDTH : 0, { velocity: vx, damping: 0.8, response: 0.3 });
      openRow = openIt ? me : null;
      if (openIt) {
        try {
          navigator.vibrate?.(4);
        } catch {
          /* no haptics here */
        }
      }
    },
    onTap: () => {
      if (openRow === me) {
        me.close();
        openRow = null;
      }
    },
  });

  function remove(): void {
    if (openRow === me) openRow = null;
    wrapper.classList.add("grow");
    requestAnimationFrame(() => {
      wrapper.classList.add("leaving");
      setTimeout(() => wrapper.remove(), prefersReducedMotion() ? 10 : 170);
    });
    options.onDelete(entry);
  }

  return wrapper;
}

export interface DayGroupOptions {
  rowFor: (entry: Entry) => HTMLElement;
}

/** Entries newest first, grouped under a day label with the day's minutes. */
export function dayGroups(entries: readonly Entry[], now: number, options: DayGroupOptions): HTMLElement[] {
  const groups = new Map<string, Entry[]>();
  for (const entry of [...entries].sort((a, b) => b.ts - a.ts)) {
    const label = dayLabel(entry.ts, now);
    groups.set(label, [...(groups.get(label) ?? []), entry]);
  }
  return [...groups].map(([label, list]) => {
    const minutes = list.reduce((sum, e) => sum + e.minutes, 0);
    return h(
      "div",
      { class: "daygroup" },
      h("div", { class: "daylabel" }, h("span", {}, label), minutes > 0 && h("span", {}, `${minutes} min`)),
      ...list.map(options.rowFor),
    );
  });
}

export interface WeekStrip {
  el: HTMLElement;
  daysLogged: number;
}

/** Seven dots, Monday first. A filled dot is a day with an entry. No streaks, no guilt. */
export function weekStrip(entries: readonly Entry[], now: number, pop = false): WeekStrip {
  const today = new Date(now);
  const dow = (today.getDay() + 6) % 7;
  const monday = new Date(today);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(today.getDate() - dow);
  const el = h("div", { class: "week", role: "list", "aria-label": "This week" });
  let daysLogged = 0;
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    const done = entries.some((e) => isSameDay(e.ts, day.getTime()));
    if (done) daysLogged++;
    const dot = h("i", { "aria-hidden": "true" });
    el.appendChild(
      h(
        "div",
        { class: `day${done ? " done" : ""}${i === dow ? " today" : ""}`, role: "listitem" },
        dot,
        h("span", {}, dayName(day.getTime()).slice(0, 3)),
      ),
    );
    if (i === dow && pop && done && !prefersReducedMotion()) {
      const s = new Spring(1.5, {
        response: 0.35,
        damping: 0.75,
        onUpdate: (v) => {
          dot.style.transform = `scale(${v})`;
        },
      });
      s.set(1);
    }
  }
  return { el, daysLogged };
}

/** The last few entries as quiet two-line rows. */
export function latelyRows(entries: readonly Entry[], now: number, limit: number, growId?: string): HTMLElement[] {
  return [...entries]
    .sort((a, b) => b.ts - a.ts)
    .slice(0, limit)
    .map((entry) => {
      const when = isSameDay(entry.ts, now) ? clockTime(entry.ts) : dayName(entry.ts).slice(0, 3);
      const row = h("div", { class: "quiet" }, h("div", { class: "etime" }, when), h("p", {}, entry.text || "(photo)"));
      return entry.id === growId ? h("div", { class: "grow" }, row) : row;
    });
}

/** Two joined buttons, the zoom control's form, for switching children or Log and Report. */
export function joinedSwitch(
  items: ReadonlyArray<{ id: string; label: string }>,
  activeId: string,
  onChange: (id: string) => void,
  label: string,
): HTMLElement {
  return h(
    "div",
    { class: "switch", role: "group", "aria-label": label },
    ...items.map((item) =>
      h(
        "button",
        { type: "button", "aria-pressed": String(item.id === activeId), onclick: () => onChange(item.id) },
        item.label,
      ),
    ),
  );
}
